// ============================================
// Manor Miser - Boot Scene
// ============================================

import Phaser from 'phaser';
import { socketManager } from '../network/SocketManager';
import { CHARACTER_TYPES } from '../shared/types';

export class BootScene extends Phaser.Scene {
  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    // Create loading bar
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    const progressBar = this.add.graphics();
    const progressBox = this.add.graphics();
    progressBox.fillStyle(0x3d2a1f, 0.8);
    progressBox.fillRect(width / 2 - 50, height / 2 - 6, 100, 12);

    const loadingText = this.add.text(width / 2, height / 2 - 14, 'Loading...', {
      fontFamily: 'Georgia, serif',
      fontSize: '6px',
      color: '#d4a855',
    });
    loadingText.setOrigin(0.5, 0.5);

    this.load.on('progress', (value: number) => {
      progressBar.clear();
      progressBar.fillStyle(0xd4a855, 1);
      progressBar.fillRect(width / 2 - 48, height / 2 - 4, 96 * value, 8);
    });

    this.load.on('complete', () => {
      progressBar.destroy();
      progressBox.destroy();
      loadingText.destroy();
    });

    // Tileset atlas (2 animation frames, 320×128 each)
    this.load.atlas(
      'tiles-atlas',
      'assets/tilesets/1bit_fantasy_pyairvander.png',
      'assets/tilesets/1bit_fantasy_pyairvander.json',
    );

    // Bitmap fonts
    this.load.image('font-pixel', 'assets/fonts/font_pixel_4x6.png');
    this.load.image('font-oldtome', 'assets/fonts/font_oldtome_letters.png');

    // UI sprites
    this.load.spritesheet('btn-unpressed', 'assets/ui/button_9_tile.png', {
      frameWidth: 48, frameHeight: 48, startFrame: 0, endFrame: 0,
    });
    this.load.spritesheet('btn-pressed', 'assets/ui/button_9_tile.png', {
      frameWidth: 48, frameHeight: 48, startFrame: 1, endFrame: 1,
    });
    this.load.image('input-bg', 'assets/ui/input_9_tile.png');

    // Player sprite atlases
    for (const charType of CHARACTER_TYPES) {
      this.load.atlas(
        `player-${charType}`,
        `assets/sprites/player_${charType}.png`,
        `assets/sprites/player_${charType}.json`,
      );
    }
    this.load.atlas('player-dead', 'assets/sprites/player_dead.png', 'assets/sprites/player_dead.json');
    this.load.atlas('coin-throw', 'assets/sprites/player_coin_throw.png', 'assets/sprites/player_coin_throw.json');

    // King sprite (manor lord — 24×24, decorative NPC)
    this.load.atlas('king', 'assets/sprites/king.png', 'assets/sprites/king.json');
  }

  async create(): Promise<void> {
    // Extract individual tileset frames as standalone textures for tilemap use
    const canvas0 = this.textures.createCanvas('tiles-frame0', 320, 128)!;
    canvas0.drawFrame('tiles-atlas', '1bit_fantasy_pyairvander 0.aseprite');
    canvas0.refresh();

    const canvas1 = this.textures.createCanvas('tiles-frame1', 320, 128)!;
    canvas1.drawFrame('tiles-atlas', '1bit_fantasy_pyairvander 1.aseprite');
    canvas1.refresh();

    // Register small pixel font (4×6) — used for all body/HUD text
    const pixelChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ.abcdefghijklmnopqrstuvwxyz\u201D\u201C!?:;, 0123456789+-/';
    this.cache.bitmapFont.add(
      'pixel',
      Phaser.GameObjects.RetroFont.Parse(this, {
        image: 'font-pixel',
        width: 4,
        height: 6,
        chars: pixelChars,
        charsPerRow: 74,
        'offset.x': 0,
        'offset.y': 0,
        'spacing.x': 0,
        'spacing.y': 0,
        lineSpacing: 0,
      })
    );

    // Register old tome font (16×24) — used for titles and large numbers
    // Parse normally, then tighten xAdvance for closer character spacing
    const oldtomeChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ.abcdefghijklmnopqrstuvwxyz\u201D\u201C!?:;, 0123456789';
    const oldtomeFont = Phaser.GameObjects.RetroFont.Parse(this, {
      image: 'font-oldtome',
      width: 16,
      height: 24,
      chars: oldtomeChars,
      charsPerRow: 71,
      'offset.x': 0,
      'offset.y': 0,
      'spacing.x': 0,
      'spacing.y': 0,
      lineSpacing: 0,
    });
    // Reduce xAdvance from 16 to 11 for tighter kerning
    const fontChars = (oldtomeFont as any).chars ?? (oldtomeFont as any).data?.chars;
    if (fontChars) {
      for (const charCode in fontChars) {
        fontChars[charCode].xAdvance = 11;
      }
    }
    this.cache.bitmapFont.add('oldtome', oldtomeFont);

    // Create animations for each character type
    for (const charType of CHARACTER_TYPES) {
      const key = `player-${charType}`;
      const prefix = `player_${charType}`;

      for (const anim of ['idle', 'betting', 'dying'] as const) {
        this.anims.create({
          key: `${charType}-${anim}`,
          frames: Array.from({ length: 8 }, (_, i) => ({
            key,
            frame: `${prefix} (${anim}) ${i}.aseprite`,
          })),
          frameRate: 8,
          repeat: anim === 'dying' ? 0 : -1,
        });
      }
    }

    // Dead animation (shared across all character types)
    this.anims.create({
      key: 'player-dead',
      frames: Array.from({ length: 8 }, (_, i) => ({
        key: 'player-dead',
        frame: `player_dead (Layer 1) ${i}.aseprite`,
      })),
      frameRate: 4,
      repeat: -1,
    });

    // Coin throw animation
    this.anims.create({
      key: 'coin-throw',
      frames: Array.from({ length: 9 }, (_, i) => ({
        key: 'coin-throw',
        frame: `player_coin_throw (Layer 1) ${i}.aseprite`,
      })),
      frameRate: 12,
      repeat: 0,
    });

    // King animations (walk + idle + jump for 4 directions, 4 frames each)
    for (const action of ['walk', 'idle', 'jump'] as const) {
      for (const dir of ['south', 'west', 'east', 'north'] as const) {
        this.anims.create({
          key: `king-${action}_${dir}`,
          frames: Array.from({ length: 4 }, (_, i) => ({
            key: 'king',
            frame: `king (${action}_${dir}) ${i}.aseprite`,
          })),
          frameRate: 6,
          repeat: -1,
        });
      }
    }

    // King attack animations (south, west, north — no east variant)
    for (const dir of ['south', 'west', 'north'] as const) {
      this.anims.create({
        key: `king-attack_${dir}`,
        frames: Array.from({ length: 4 }, (_, i) => ({
          key: 'king',
          frame: `king (attack_${dir}) ${i}.aseprite`,
        })),
        frameRate: 8,
        repeat: 0, // play once
      });
    }

    // Connect to server
    try {
      await socketManager.connect();
      console.log('[Boot] Connected to server');
    } catch (error) {
      console.error('[Boot] Failed to connect:', error);
    }

    // Hide the HTML loading screen
    const loading = document.getElementById('loading');
    if (loading) {
      loading.classList.add('hidden');
    }

    this.scene.start('MenuScene');
  }
}
